/**
 * Log In page (API mode). Posts the Email ID and password to the API, which sets an httpOnly session cookie;
 * the app then reloads at the page the user came from. In demo (mock) mode there is nothing to log in to.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useAuth } from '@/app/AppContext';
import { Field } from '@/ui/kit';
import { LOGIN_PATH } from './authAdapter';

/** Remember Me keeps only the Email ID (never the password) in this browser. */
export const REMEMBER_KEY = 'easysaas.login.email';
const EMAIL_MAX = 254;
const PASSWORD_MAX = 128;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Only same-site paths, so a crafted link cannot send the user to another website after Log In. */
export const safeNext = (next: string | null): string =>
  next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\') && !next.startsWith(LOGIN_PATH) ? next : '/dashboard';

const readRemembered = (): string => {
  try {
    return localStorage.getItem(REMEMBER_KEY) || '';
  } catch {
    return '';
  }
};

type Errors = { email?: string; password?: string; form?: string };

export function LoginPage({ onSuccess = (url: string) => window.location.replace(url) }: { onSuccess?: (url: string) => void }) {
  const auth = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const remembered = readRemembered();
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(!!remembered);
  const [show, setShow] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);

  if (auth.adapter.kind === 'mock' || !auth.adapter.logInWithPassword) return <Navigate to="/dashboard" replace />;
  if (auth.user) return <Navigate to={next} replace />;
  if (auth.loading) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const err: Errors = {};
    const em = email.trim();
    if (!em) err.email = 'Email ID is required.';
    else if (!EMAIL_RE.test(em)) err.email = 'Enter a valid Email ID.';
    if (!password.trim()) err.password = 'Password is required.';
    setErrors(err);
    if (err.email || err.password) return;
    setBusy(true);
    try {
      await auth.adapter.logInWithPassword!(em, password, remember);
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, em);
        else localStorage.removeItem(REMEMBER_KEY);
      } catch {
        /* preference only */
      }
      onSuccess(next);
    } catch (x) {
      const msg = x instanceof Error ? x.message : 'Log In failed. Please try again.';
      const status = (x as { status?: number }).status;
      setErrors(status === 401 ? { password: msg } : { form: msg });
      setBusy(false);
    }
  };

  return (
    <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 16, background: 'var(--bg)' }}>
      <div className="panel" style={{ width: '100%', maxWidth: 400, margin: 0 }}>
        <div className="panel-b" style={{ padding: '24px 24px 20px' }}>
          <div className="brand" style={{ padding: '0 0 14px' }}>
            <div className="logo">✓</div>
            <div>
              Easy SaaS
              <div className="hint" style={{ fontWeight: 400 }}>
                Project tracker
              </div>
            </div>
          </div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px', fontWeight: 700 }}>Log In</h1>
          <p className="hint" style={{ margin: '0 0 16px' }}>
            Log In with your work Email ID and password.
          </p>
          <form onSubmit={submit} noValidate>
            <Field label="Email ID" required error={errors.email}>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter Email ID"
                maxLength={EMAIL_MAX}
                autoComplete="username"
                aria-label="Email ID"
                aria-invalid={!!errors.email}
                autoFocus={!remembered}
              />
            </Field>
            <Field label="Password" required error={errors.password}>
              <div style={{ position: 'relative' }}>
                <input
                  type={show ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter Password"
                  maxLength={PASSWORD_MAX}
                  autoComplete="current-password"
                  aria-label="Password"
                  aria-invalid={!!errors.password}
                  autoFocus={!!remembered}
                  style={{ paddingRight: 42 }}
                />
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? 'Hide password' : 'Show password'}
                  aria-pressed={show}
                  data-tip={show ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', border: 0, background: 'transparent' }}
                >
                  <EyeIcon off={show} />
                </button>
              </div>
            </Field>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', margin: '-2px 0 14px' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, fontWeight: 500, color: 'var(--text)', cursor: 'pointer' }}>
                <input type="checkbox" name="remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ width: 'auto' }} />
                Remember Me
              </label>
              <button type="button" onClick={() => setForgot((f) => !f)} aria-expanded={forgot} style={{ background: 'none', border: 0, padding: 0, color: 'var(--primary)', cursor: 'pointer', font: 'inherit' }}>
                Forgot Password
              </button>
            </div>
            {forgot ? (
              <p className="hint" role="note" style={{ margin: '0 0 14px' }}>
                Ask an administrator to reset your password.
              </p>
            ) : null}
            {errors.form ? (
              <div className="err" role="alert" style={{ display: 'block', margin: '0 0 12px' }}>
                {errors.form}
              </div>
            ) : null}
            <button type="submit" className="btn primary" disabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
              {busy ? 'Logging In…' : 'Log In'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <path d="M3 3l18 18" /> : null}
    </svg>
  );
}
